<template>
    <slot name="activator" :props="activatorSlotParams" />
    <v-overlay
        v-model="dialog"
        :width="mobile ? '100%' : undefined"
        :class="{ 'align-center': !mobile, 'align-end': mobile, 'justify-center': true }"
    >
        <!-- <div
            class="d-flex space-x-2"
            v-if="!mobile"
            :style="{
                width: width,
                height: height,
                maxWidth: maxWidth,
                minWidth: minWidth,
                padding: '4px',
            }"
        > -->
        <v-card
            class="d-flex flex-column align-center justify-center"
            :style="{
                width: width,
                maxWidth: maxWidth,
                minWidth: minWidth,
                padding: '16px',
            }"
            rounded="lg"
        >
            <!-- Dialog Title -->
            <div v-if="title" class="text-h6 text-center mb-4">
                {{ title }}
            </div>

            <!-- Dialog Text -->
            <div v-if="text" class="text-subtitle-2 text-center mb-4">
                {{ text }}
            </div>
        </v-card>
        <!-- Actions -->
        <slot name="actions" :props="actionSlotParams" class="mt-2">
            <v-btn-primary
                v-if="hasValidButton"
                block
                size="x-large"
                @click="validEvent"
                :loading="loading || validLoading"
                :data-cy="prefixDataCy + '_action'"
            >
                {{ validText }}
            </v-btn-primary>
        </slot>
        <!-- <v-hover>
            <template #default="{ isHovering, props }">
                <div class="rounded-pill oss-dialog-close ml-2" v-bind="props">
                    <v-btn
                        icon="$oss-close"
                        @click="dialog = false"
                        color="white"
                        variant="flat"
                        size="large"
                        rounded="pill"
                        :data-cy="prefixDataCy + '_close'"
                    ></v-btn>
                    <v-scroll-y-transition>
                        <v-btn
                            v-if="isHovering"
                            icon="$fa-minus"
                            @click="dialog = false"
                            color="white"
                            variant="flat"
                            size="large"
                            rounded="pill"
                            :data-cy="prefixDataCy + '_minus'"
                        ></v-btn>
                    </v-scroll-y-transition>
                </div>
            </template>
        </v-hover>
        </div> -->
    </v-overlay>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance } from "vue";
import { DialogAction } from "@/services/dialogAction";
import { useDisplay } from "vuetify";

interface DialogProps {
    /** The title displayed at the top of the dialog */
    title: {
        type: String;
        required: false;
        default: "";
    };
    /** The main text displayed in the dialog */
    text: {
        type: String;
        required: false;
        default: "";
    };
    /** Whether the dialog is persistent (cannot close by clicking outside) */
    persistent: {
        type: Boolean;
        required: false;
        default: true;
    };
    /** The width of the dialog */
    heigth: {
        type: String;
        required: false;
        default: "40vw";
    };
    /** The width of the dialog */
    width: {
        type: String;
        required: false;
        default: "40vw";
    };
    /** The max width of the dialog */
    maxWidth: {
        type: String;
        required: false;
        default: "60vw";
    };
    /** The min width of the dialog */
    minWidth: {
        type: String;
        required: false;
        default: "20vw";
    };
    /** The text displayed on the validation button */
    validText: {
        type: String;
        required: false;
        default: "Valider";
    };
    /** Whether the validation button should be disabled */
    validDisabled: {
        type: Boolean;
        required: false;
        default: false;
    };
    /** Whether the dialog is in a loading state */
    loading: {
        type: Boolean;
        required: false;
        default: false;
    };
    validLoading?: boolean;
    /** Actions available in the dialog */
    actions?: { action: DialogAction };

    prefixDataCy?: string;
}

const { mobile } = useDisplay();

const props = defineProps<DialogProps>();

const emits = defineEmits(["valid", "close"]);

const currentInstance = getCurrentInstance();

const $props = computed<Record<string, unknown>>(() => currentInstance?.vnode?.props || {});

const shouldEmitValidation = computed<boolean>(() => !!$props.value.onValid);

const hasFunctionInActions = computed(
    () =>
        !!props.actions?.action?.function &&
        props.actions?.action?.function instanceof Function &&
        props.actions?.action?.display,
);

const hasValidButton = computed(() => shouldEmitValidation.value || hasFunctionInActions.value);

const loading = computed(() => props.loading || localLoading.value);

const dialog = defineModel<boolean>({
    default: false,
    set: (value) => {
        if (!value) {
            emits("close");
        }
        return value;
    },
    get: (value) => value,
});

const localLoading = ref(false);

const isLoading = computed(() => props.loading || localLoading.value);

const validEvent = async () => {
    localLoading.value = true;
    try {
        emits("valid");
    } catch (error) {
        console.error("Erreur lors de la validation :", error);
    } finally {
        localLoading.value = false;
    }
};

const closeDialog = () => {
    dialog.value = false;
};

// ---- SLOT PARAMS ----

const activatorSlotParams = computed(() => {
    return {
        onClick: () => {
            dialog.value = true;
        },
    };
});

const actionSlotParams = computed(() => {
    return {
        onClick: validEvent,
    };
});
</script>

<style scoped lang="scss">
.v-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
}

.oss-dialog-close {
    display: grid;
    background: #fff;
    border-radius: 50px !important;
    transition-duration: 0.3s;
    height: 56px;
}

.oss-dialog-close:hover {
    height: 112px;
}
</style>
